/**
 * UI Store
 * Manages global UI state like editor mode, modals, sidebars, etc.
 */

import { create } from 'zustand'

interface UIState {
	/**
	 * Editor Mode - Optimizes UI for form editing
	 * When true:
	 * - Header height reduced to 25px with minimal content
	 * - Footer shows Cancel/Save buttons instead of navigation
	 * - Main content area maximized for editing
	 */
	editorMode: boolean
	setEditorMode: (enabled: boolean) => void

	/**
	 * Editor Mode Text - Breadcrumb text shown in minimal header
	 * Format: "EKONAVI / {Project Name} / Editar Projeto"
	 * Project name is truncated to 30 characters max
	 */
	editorModeText: string
	setEditorModeText: (text: string) => void

	/**
	 * Is form saving
	 */
	isSaving: boolean
	setIsSaving: (isSaving: boolean) => void

	/**
	 * Editor Mode Actions
	 * Callback functions for Save and Cancel actions
	 * Set by form components to handle their specific actions
	 */
	editorActions: {
		onSave?: () => void
		onCancel?: () => void
		saveLabel?: string
		cancelLabel?: string
		isSaving?: boolean
	}
	setEditorActions: (actions: UIState['editorActions']) => void

	/**
	 * Clear editor mode and actions
	 * Should be called when leaving edit/create pages
	 */
	clearEditorMode: () => void
}

export const useUIStore = create<UIState>((set) => ({
	editorMode: false,
	setEditorMode: (enabled) => set({ editorMode: enabled }),

	isSaving: false,
	setIsSaving: (isSaving) => set({ isSaving }),

	editorModeText: '',
	setEditorModeText: (text) => set({ editorModeText: text }),

	editorActions: {},
	setEditorActions: (actions) =>
		set((state) => ({
			editorActions: { ...state.editorActions, ...actions },
		})),

	clearEditorMode: () =>
		set({
			editorMode: false,
			editorModeText: '',
			editorActions: {},
		}),
}))
